import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import { fetchChatbots } from "../../features/ai_chatbots/aiChatbotsSlice";
import { AIChatbot } from "../../features/ai_chatbots/aiChatbotsService";
import { Card, Button, Spinner, Alert, Row, Col } from "react-bootstrap";
import { Icon } from "../../components/common/Icon";
import { FaRobot, FaTrash, FaPlay } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import AIChatbotDetailModal from "../ai_chatbots/components/AIChatbotDetailModal";
import AIChatbotWidgetModal from "../ai_chatbots/components/AIChatbotWidgetModal";
import AIChatbotTestModal from "../ai_chatbots/components/AIChatbotTestModal";
import AIChatbotMessagesModal from "../ai_chatbots/components/AIChatbotMessagesModal";
import companiesService, { Company } from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";

import ConfirmationModal from "../../components/common/ConfirmationModal";
import { deleteChatbot, setPage } from "../../features/ai_chatbots/aiChatbotsSlice";
import { isAdmin, isAgencyOwner, isCompanyOwner, isMarketingSpecialist, isCompanySpecialist } from "../../utils/permissions";
import { startWidgetPreview } from "../ai_chatbots/components/widgetPreview";
import Pagination from "../../components/common/Pagination";
import { setSelectedClient, setSelectedAgency } from "../../features/settings/settingsSlice";
import { getClientsByAgencyId } from "../../features/companies/companiesSlice";
import { clearChatbotCache } from "../../features/ai_chatbots/aiChatbotsService";
import { startChatbotTour } from "../../utils/chatbotTour";
import { TbCirclePlus, TbPlayerEject, TbCode, TbRefresh, TbEdit, TbCopy, TbExternalLink } from "react-icons/tb";
import AIChatbotCopyModal from "../ai_chatbots/components/AIChatbotCopyModal";
import CreateOrCopyChoiceModal from "../ai_chatbots/components/CreateOrCopyChoiceModal";
import CopyFromChatbotModal from "../ai_chatbots/components/CopyFromChatbotModal";
import { RiChatVoiceAiFill } from "react-icons/ri";
import { TbMessage } from "react-icons/tb";
import { RiChatVoiceAiLine } from "react-icons/ri";
import { trackEvent } from "../../utils/analytics";

// Must match AIMarketingBackend/app/utils/slug.py so the URL we render here
// resolves to the same public page the backend serves.
const TURKISH_MAP: Record<string, string> = {
  ç: "c", Ç: "c", ğ: "g", Ğ: "g", ı: "i", I: "i", İ: "i",
  ö: "o", Ö: "o", ş: "s", Ş: "s", ü: "u", Ü: "u",
};

const slugifyCompany = (text?: string | null): string => {
  if (!text) return "company";
  const replaced = text.replace(/[çÇğĞıIİöÖşŞüÜ]/g, (ch) => TURKISH_MAP[ch] ?? ch);
  const normalized = replaced.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  const slug = normalized
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return slug || "company";
};

const buildPublicAgentUrl = (chatbot: AIChatbot): string | null => {
  if (!chatbot.public_page_enabled || !chatbot.widget_key) return null;
  const base =
    (process.env.REACT_APP_PUBLIC_URL as string | undefined) ||
    (typeof window !== "undefined" ? window.location.origin : "https://meylo.app");
  return `${base}/${slugifyCompany(chatbot.company_name)}/${chatbot.widget_key}`;
};

const AIChatbotsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { chatbots, isLoading, error, page, limit, totalCount } = useSelector((s: RootState) => s.aiChatbots);
  const { selectedAgencyId, selectedClientId } = useSelector((s: RootState) => s.settings);
  const { clients } = useSelector((s: RootState) => s.companies);
  const { user } = useSelector((s: RootState) => s.auth);
  const { t } = useTranslation();

  const [search] = useState("");
  const [myCompanyId, setMyCompanyId] = useState<number | undefined>(undefined);
  const [selectedChatbotId, setSelectedChatbotId] = useState<number | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [singleCompanies, setSingleCompanies] = useState<Company[]>([]);
  const [selectedSingleCompanyId, setSelectedSingleCompanyId] = useState<number | undefined>(undefined);
  const [showClearCacheModal, setShowClearCacheModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [chatbotForCopy, setChatbotForCopy] = useState<AIChatbot | null>(null);
  const [showCreateOrCopyChoiceModal, setShowCreateOrCopyChoiceModal] = useState(false);
  const [showCopyFromModal, setShowCopyFromModal] = useState(false);

  // Load single company id for company owner if not selected; for company_specialist load from my-companies
  useEffect(() => {
    const roles = user?.roles || [];
    const isOwner = roles.includes("company_owner");
    const isSpecialist = roles.includes("company_specialist");
    (async () => {
      try {
        if (!selectedClientId && myCompanyId === undefined) {
          if (isOwner) {
            const c = await companiesService.getMyCompany();
            setMyCompanyId(c?.id);
          } else if (isSpecialist) {
            const list = await usersService.getMyCompanies();
            const single = Array.isArray(list) ? list.find((c: any) => c.company_type === "single") : undefined;
            if (single?.id) setMyCompanyId(single.id);
          }
        }
      } catch {
        // ignore
      }
    })();
  }, [user, selectedClientId, myCompanyId]);

  // Load selectable companies for Admin/Super Admin
  useEffect(() => {
    const loadCompanies = async () => {
      if (!isAdmin(user)) return;
      try {
        const { companies } = await companiesService.getCompanies({ limit: 1000 });
        setSingleCompanies(companies || []);
        // Do not auto-select first company
      } catch {
        setSingleCompanies([]);
      }
    };
    loadCompanies();
  }, [user]);

  // Sync Redux selectedClientId to local company selector for admin (keeps selection after returning from edit)
  useEffect(() => {
    if (isAdmin(user)) {
      setSelectedSingleCompanyId(selectedClientId);
    }
  }, [selectedClientId, user]);

  const roles = user?.roles || [];
  const isOwner = roles.includes("company_owner");
  const isAgencyOwnerRole = roles.includes("agency_owner");

  // Agency Owner: ensure agency is set, then load client companies for the filter dropdown
  useEffect(() => {
    if (!isAgencyOwnerRole) return;
    (async () => {
      if (selectedAgencyId) {
        dispatch(getClientsByAgencyId(selectedAgencyId));
        return;
      }
      try {
        const agency = await companiesService.getMyAgency();
        if (agency?.id) {
          dispatch(setSelectedAgency(agency.id));
          dispatch(getClientsByAgencyId(agency.id));
        }
      } catch {
        // ignore
      }
    })();
  }, [isAgencyOwnerRole, selectedAgencyId, dispatch]);

  // Force single company filter (company_type single is implied by selectedClientId in this area)
  // Admin/Superadmin: no module filter → see all chatbots. Agency Owner: module=marketing. Others: ai_tools
  const effectiveFilters = useMemo(
    () => ({
      company_id: isAdmin(user)
        ? (selectedSingleCompanyId ?? selectedClientId)
        : isAgencyOwnerRole
          ? (selectedClientId ?? undefined)
          : (selectedClientId || myCompanyId),
      search: search || undefined,
      module: isAdmin(user) ? undefined : (isAgencyOwnerRole ? "marketing" : "ai_tools"),
    }),
    [selectedClientId, myCompanyId, selectedSingleCompanyId, search, user, isAgencyOwnerRole]
  );
  const waitingForCompany = isOwner && !selectedClientId && !myCompanyId;

  useEffect(() => {
    if (waitingForCompany) return; // defer until company is known for company roles
    const params: any = { ...effectiveFilters, page, limit };
    dispatch(fetchChatbots(params));
  }, [dispatch, page, limit, effectiveFilters, waitingForCompany]);

  const isCompanyOwnerRole = isCompanyOwner(user);
  const canEdit = isAdmin(user) || isAgencyOwner(user) || isCompanyOwnerRole;
  const canDelete = isAdmin(user);
  const canUseOps = isAdmin(user) || isAgencyOwner(user) || isCompanyOwnerRole || isMarketingSpecialist(user) || isCompanySpecialist(user);

  const handleClearCacheClick = (id: number) => {
    setSelectedChatbotId(id);
    setShowClearCacheModal(true);
  };

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const confirmClearCache = async () => {
    if (!selectedChatbotId) return;
    try {
      await clearChatbotCache(selectedChatbotId);
    } finally {
      setShowClearCacheModal(false);
    }
  };

  const renderGrid = (items: AIChatbot[]) => (
    <Row className="chatbots-grid">
      {[...items].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map(chatbot => (
        <Col key={chatbot.id} xxl={4} xl={6} lg={12} md={12} sm={12} className="mb-4">
          <Card className="chatbot-card">
            <Card.Header className="chatbot-header">
              <div className="chatbot-header-left">
                <div className="chatbot-icon">
                  <RiChatVoiceAiFill />
                </div>
                <div className="chatbot-name">{chatbot.name}</div>
              </div>
              <div className="chatbot-header-right">
                <span className={`status-badge ${chatbot.is_active ? "active" : "inactive"}`}>
                  <span className="status-indicator"></span>
                  {chatbot.is_active ? t("active") : t("inactive")}
                </span>
              </div>
            </Card.Header>
            <Card.Body className="chatbot-body">
              <p className="chatbot-description">{chatbot.description || t("no_description")}</p>
              <div className="chatbot-details">
                <div className="detail-item">
                  <span className="detail-label">{t("model")}:</span>
                  <span className="detail-value">{chatbot.model}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t("created")}:</span>
                  <span className="detail-value">{new Date(chatbot.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </Card.Body>
            <Card.Footer className="chatbot-footer">
              <div className="chatbot-button-grid top">
                {canUseOps && (
                  <Button
                    className="chatbot-card-btn btn-messages"
                    onClick={() => {
                      trackEvent("ai_agent_messages_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      setSelectedChatbotId(chatbot.id);
                      setShowSessionsModal(true);
                    }}
                  >
                    <TbMessage />
                    <span> {t("messages")}</span>
                  </Button>
                )}
                {canUseOps && (
                  <Button
                    size="sm"
                    className="chatbot-card-btn btn-test"
                    onClick={() => {
                      trackEvent("ai_agent_test_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      setSelectedChatbotId(chatbot.id);
                      setShowTestModal(true);
                    }}
                  >
                    <RiChatVoiceAiLine />
                    <span> {t("test")}</span>
                  </Button>
                )}
                {canUseOps && (
                  <Button
                    size="sm"
                    className="chatbot-card-btn btn-widget"
                    onClick={() => {
                      trackEvent("ai_agent_add_to_website_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      setSelectedChatbotId(chatbot.id);
                      setShowWidgetModal(true);
                    }}
                  >
                    <TbCode />
                    <span> {t("add_to_website")}</span>
                  </Button>
                )}
                {canUseOps && (
                  <Button
                    size="sm"
                    className="chatbot-card-btn btn-preview"
                    onClick={() => {
                      trackEvent("ai_agent_live_view_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      startWidgetPreview(chatbot as any);
                    }}
                  >
                    <TbPlayerEject />
                    <span> {t("live_view")}</span>
                  </Button>
                )}
                {canUseOps && chatbot.public_page_enabled && (() => {
                  const publicUrl = buildPublicAgentUrl(chatbot);
                  if (!publicUrl) return null;
                  return (
                    <Button
                      size="sm"
                      className="chatbot-card-btn btn-chat-page"
                      onClick={() => {
                        trackEvent("ai_agent_chat_page_click", {
                          category: "ai_agents",
                          label: `chatbot_id_${chatbot.id}`,
                        });
                        window.open(publicUrl, "_blank", "noopener,noreferrer");
                      }}
                      title={publicUrl}
                    >
                      <TbExternalLink />
                      <span> {t("chat_page")}</span>
                    </Button>
                  );
                })()}
                {canUseOps && (
                  <Button
                    size="sm"
                    className="chatbot-card-btn btn-clear-cache"
                    onClick={() => {
                      trackEvent("ai_agent_refresh_training_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      handleClearCacheClick(chatbot.id);
                    }}
                  >
                    <TbRefresh />
                    <span> {t("refresh_training")}</span>
                  </Button>
                )}
                {canEdit && (
                  <Button
                    size="sm"
                    className="chatbot-card-btn btn-edit"
                    onClick={() => {
                      trackEvent("ai_agent_edit_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      navigate(`/ai-chatbots/${chatbot.id}/edit`);
                    }}
                  >
                    <TbEdit />
                    <span> {t("edit")}</span>
                  </Button>
                )}
                {canEdit && (
                  <Button
                    size="sm"
                    className="chatbot-card-btn btn-copy"
                    onClick={() => {
                      trackEvent("ai_agent_copy_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      setChatbotForCopy(chatbot);
                      setShowCopyModal(true);
                    }}
                  >
                    <TbCopy />
                    <span> {t("copy")}</span>
                  </Button>
                )}
                {canDelete && (
                  <Button
                    size="sm"
                    className={`action-btn danger`}
                    onClick={() => {
                      trackEvent("ai_agent_delete_click", {
                        category: "ai_agents",
                        label: `chatbot_id_${chatbot.id}`,
                      });
                      setSelectedChatbotId(chatbot.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    <Icon icon={FaTrash} className="me-1" />
                    {t("delete")}
                  </Button>
                )}
              </div>
            </Card.Footer>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const handleOpenCreate = async () => {
    let targetId = effectiveFilters.company_id || myCompanyId;
    if (!targetId) {
      try {
        const c = await companiesService.getMyCompany();
        if (c?.id) {
          targetId = c.id;
          setMyCompanyId(c.id);
        }
      } catch { }
    }
    navigate("/ai-chatbots/new", { state: { companyId: targetId } });
  };

  const handleCreateNewChatbotClick = () => {
    trackEvent("ai_agent_create_click", {
      category: "ai_agents",
    });
    setShowCreateOrCopyChoiceModal(true);
  };

  const handleChoiceCreateNew = () => {
    trackEvent("ai_agent_choice_create_new_click", {
      category: "ai_agents",
    });
    handleOpenCreate();
  };

  const handleChoiceCopyFrom = () => {
    trackEvent("ai_agent_choice_copy_from_click", {
      category: "ai_agents",
    });
    setShowCopyFromModal(true);
  };

  return (
    <div className="chatbots-page">
      <div className="page-header-card">
        <div className="page-header-text">
          <h1>{t("ai_chatbots")}</h1>
          <p>{t("manage_your_ai_powered_chatbot_assistants")}</p>
        </div>
        <div className="page-header-right">
          {isAdmin(user) && (
            <div className="company-selector">
              <select
                className="form-select"
                value={selectedSingleCompanyId || ""}
                onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedSingleCompanyId(val);
                  dispatch(setSelectedClient(val));
                }}
              >
                <option value="">{t("select_company")}</option>
                {singleCompanies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isAgencyOwnerRole && (
            <div className="company-selector">
              <select
                className="form-select"
                value={selectedClientId ?? ""}
                onChange={e => {
                  const val = e.target.value ? Number(e.target.value) : undefined;
                  dispatch(setSelectedClient(val));
                }}
              >
                <option value="">{t("all_client_companies")}</option>
                {(clients || []).map((c: Company) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Button
            className="chatbots-actions-tour mt-2 mb-2 btn btn-primary"
            onClick={() => startChatbotTour(t)}
            name="chatbot_tour_btn"
          >
            <FaPlay />
            <span> {t("tour.start")}</span>
          </Button>
          {(canEdit || canDelete) && (
            <Button className="page-header-btn" onClick={handleCreateNewChatbotClick} name="create_chatbot_btn">
              <TbCirclePlus />
              <span> {t("create_new_chatbot")}</span>
            </Button>
          )}

          {/* <div className="search-box">
              <i className="bi bi-search" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search_placeholder") as string}
              />
            </div> */}
        </div>
      </div>

      <div className="content">
        {(isLoading || waitingForCompany) && (
          <div className="loading-container">
            <Spinner animation="border" className="loading-spinner" />
          </div>
        )}
        {!waitingForCompany && error && <Alert className="error-alert">{error}</Alert>}

        {!isLoading && !waitingForCompany && !error && chatbots.length > 0 && (
          <>
            {renderGrid(chatbots)}
            <div className="d-flex justify-content-center mt-4">
              <Pagination
                currentPage={page}
                pageSize={limit}
                totalCount={totalCount}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
        {!isLoading && !waitingForCompany && !error && chatbots.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <Icon icon={FaRobot} />
            </div>
            <h3 className="empty-title">{t("not_found")}</h3>
            <p className="empty-description">{t("no_chatbots_found")}</p>
            {(canEdit || canDelete) && (
              <Button className="btn-primary" onClick={handleCreateNewChatbotClick}>
                {t("add_new_ai_chatbot")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Modals re-used from marketing dashboard components */}
      {showDetailModal && <AIChatbotDetailModal show={showDetailModal} onHide={() => setShowDetailModal(false)} chatbotId={selectedChatbotId} />}
      {showWidgetModal && <AIChatbotWidgetModal show={showWidgetModal} onHide={() => setShowWidgetModal(false)} chatbotId={selectedChatbotId} />}
      {showTestModal && (
        <AIChatbotTestModal
          show={showTestModal}
          onHide={() => setShowTestModal(false)}
          chatbot={selectedChatbotId ? chatbots.find(c => c.id === selectedChatbotId) || null : null}
        />
      )}

      {/* Messages Modal */}
      {showSessionsModal && selectedChatbotId && (
        <AIChatbotMessagesModal
          show={showSessionsModal}
          onHide={() => setShowSessionsModal(false)}
          chatbotId={selectedChatbotId}
          chatbotName={selectedChatbotId ? chatbots.find(c => c.id === selectedChatbotId)?.name || "" : ""}
        />
      )}



      <ConfirmationModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (selectedChatbotId) {
            dispatch(deleteChatbot(selectedChatbotId) as any)
              .unwrap()
              .then(() => {
                setShowDeleteModal(false);
                dispatch(fetchChatbots({ ...effectiveFilters, page, limit }));
              })
              .catch(() => setShowDeleteModal(false));
          }
        }}
        title={t("delete_chatbot")}
        message={t("are_you_sure_delete_chatbot")}
        confirmText={"delete"}
      />

      <ConfirmationModal
        show={showClearCacheModal}
        onCancel={() => setShowClearCacheModal(false)}
        onConfirm={confirmClearCache}
        title={t("refresh_training")}
        message={t("clear_cache_confirm_message")}
        confirmText="refresh_training"
        cancelText={"cancel"}
      />

      <AIChatbotCopyModal
        show={showCopyModal}
        onHide={() => {
          setShowCopyModal(false);
          setChatbotForCopy(null);
        }}
        chatbot={chatbotForCopy}
        onSuccess={() => dispatch(fetchChatbots({ ...effectiveFilters, page, limit }))}
      />

      <CreateOrCopyChoiceModal
        show={showCreateOrCopyChoiceModal}
        onHide={() => setShowCreateOrCopyChoiceModal(false)}
        onCreateNew={handleChoiceCreateNew}
        onCopyFrom={handleChoiceCopyFrom}
      />

      <CopyFromChatbotModal
        show={showCopyFromModal}
        onHide={() => setShowCopyFromModal(false)}
        onSuccess={() => dispatch(fetchChatbots({ ...effectiveFilters, page, limit }))}
      />
    </div>
  );
};

export default AIChatbotsPage;
